import { useEffect, useMemo, useState } from 'react';
import { deleteDoc, doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import {
  ArrowDown,
  ArrowUp,
  Backpack,
  Eye,
  EyeOff,
  Plus,
  Save,
  Trash2,
  WandSparkles
} from 'lucide-react';
import PageHeader from '../../components/ui/PageHeader';
import { useAuth } from '../../core/auth/AuthProvider';
import { db } from '../../core/firebase/firebase';
import {
  DEFAULT_SUBJECTS,
  SCHOOL_SUPPLY_NOTICES,
  SUPPLY_LEVELS,
  SUPPLY_SECTIONS,
  currentSchoolYear,
  getSupplyPreset,
  levelLabel,
  schoolYearLabel,
  schoolYearOptions,
  suppliesListId
} from './schoolSuppliesUtils';

function newItem() {
  return {
    id: `item_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    name: '',
    quantity: 1,
    section: SUPPLY_SECTIONS[0],
    subject: '',
    cover: '',
    note: ''
  };
}

function normalizeLoadedItem(item) {
  return {
    id: item.id || `item_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    name: item.name || '',
    quantity: Number(item.quantity) || 1,
    section: item.section || item.category || SUPPLY_SECTIONS[0],
    subject: item.subject || '',
    cover: item.cover || '',
    note: item.note || ''
  };
}

function cleanItems(items) {
  return items
    .filter((item) => item.name.trim())
    .map((item, index) => ({
      id: item.id,
      name: item.name.trim(),
      quantity: Math.max(1, Number(item.quantity) || 1),
      section: item.section || SUPPLY_SECTIONS[0],
      subject: item.subject?.trim() || '',
      cover: item.cover?.trim() || '',
      note: item.note?.trim() || '',
      order: index
    }));
}

export default function SchoolSuppliesPage() {
  const { user, profile } = useAuth();
  const [schoolYear, setSchoolYear] = useState(currentSchoolYear());
  const [levelKey, setLevelKey] = useState('1ere');
  const [items, setItems] = useState([]);
  const [published, setPublished] = useState(false);
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const listId = suppliesListId(schoolYear, levelKey);
  const years = schoolYearOptions(schoolYear);
  const preset = getSupplyPreset(levelKey);

  useEffect(() => {
    setLoading(true);
    setError('');
    setMessage('');
    const listRef = doc(db, 'schoolSupplies', listId);
    return onSnapshot(listRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        const nextItems = Array.isArray(data.items)
          ? [...data.items]
              .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
              .map(normalizeLoadedItem)
          : [];
        setItems(nextItems);
        setPublished(data.published === true);
        setExists(true);
      } else {
        setItems([]);
        setPublished(false);
        setExists(false);
      }
      setLoading(false);
    }, (loadError) => {
      console.error(loadError);
      setError('Impossible de charger cette liste de fournitures.');
      setLoading(false);
    });
  }, [listId]);

  const groupedPreview = useMemo(() => {
    const valid = items.filter((item) => item.name.trim());
    return SUPPLY_SECTIONS.map((section) => {
      const rows = valid.filter((item) => (item.section || SUPPLY_SECTIONS[0]) === section);
      return [section, rows];
    }).filter(([, rows]) => rows.length);
  }, [items]);

  function updateItem(id, field, value) {
    setItems((current) => current.map((item) => item.id === id ? { ...item, [field]: value } : item));
  }

  function removeItem(id) {
    setItems((current) => current.filter((item) => item.id !== id));
  }

  function moveItem(id, direction) {
    setItems((current) => {
      const index = current.findIndex((item) => item.id === id);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= current.length) return current;
      const copy = [...current];
      [copy[index], copy[target]] = [copy[target], copy[index]];
      return copy;
    });
  }

  function loadPreset() {
    if (!preset.length) return;
    if (items.length && !window.confirm(`Remplacer la liste actuelle par la liste retranscrite pour ${levelLabel(levelKey)} ?`)) return;
    setItems(preset);
    setMessage('Liste retranscrite chargée. Vérifiez-la puis enregistrez-la quand elle est prête.');
    setError('');
  }

  async function saveList() {
    const validItems = cleanItems(items);
    if (!validItems.length) {
      setError('Ajoutez au moins une fourniture avant d’enregistrer.');
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        listId,
        levelKey,
        levelLabel: levelLabel(levelKey),
        schoolYear,
        published,
        items: validItems,
        notices: SCHOOL_SUPPLY_NOTICES,
        updatedAt: serverTimestamp(),
        updatedByUid: user?.uid || null,
        updatedByName: profile?.fullname || profile?.fullName || 'Administration'
      };
      if (!exists) payload.createdAt = serverTimestamp();
      await setDoc(doc(db, 'schoolSupplies', listId), payload, { merge: true });
      setMessage(published ? 'Liste enregistrée et visible aux parents.' : 'Brouillon enregistré.');
    } catch (saveError) {
      console.error(saveError);
      setError(`Enregistrement impossible : ${saveError.message}`);
    } finally {
      setSaving(false);
    }
  }

  async function deleteList() {
    if (!exists) return;
    if (!window.confirm(`Supprimer la liste ${levelLabel(levelKey)} — ${schoolYearLabel(schoolYear)} ?`)) return;
    setSaving(true);
    setError('');
    try {
      await deleteDoc(doc(db, 'schoolSupplies', listId));
      setMessage('Liste supprimée.');
    } catch (deleteError) {
      console.error(deleteError);
      setError(`Suppression impossible : ${deleteError.message}`);
    } finally {
      setSaving(false);
    }
  }

  return <>
    <PageHeader
      eyebrow="PARENTS"
      title="Fournitures scolaires"
      description="Créez les listes par classe, section et matière. Les parents les verront comme des checklists interactives."
    />

    {message && <div className="alert supplies-admin-success"><Backpack size={17}/>{message}</div>}
    {error && <div className="alert alert-error">{error}</div>}

    <section className="panel supplies-admin-toolbar">
      <label>
        <span>Année scolaire</span>
        <select value={schoolYear} onChange={(event) => setSchoolYear(event.target.value)}>
          {years.map((year) => <option key={year} value={year}>{schoolYearLabel(year)}</option>)}
        </select>
      </label>
      <label>
        <span>Classe</span>
        <select value={levelKey} onChange={(event) => setLevelKey(event.target.value)}>
          {SUPPLY_LEVELS.map((level) => <option key={level.key} value={level.key}>{level.label}</option>)}
        </select>
      </label>
      <div className={`supplies-publish-status ${published ? 'published' : 'draft'}`}>
        {published ? <Eye size={17}/> : <EyeOff size={17}/>} 
        <div><strong>{published ? 'Visible aux parents' : 'Brouillon'}</strong><span>{exists ? 'Liste enregistrée' : 'Pas encore enregistrée'}</span></div>
      </div>
    </section>

    {loading ? <div className="panel empty-state"><strong>Chargement…</strong><p>Récupération de la liste.</p></div> : (
      <section className="supplies-admin-layout">
        <article className="panel supplies-editor-panel">
          <div className="supplies-editor-heading">
            <div>
              <p className="eyebrow">LISTE À MODIFIER</p>
              <h2>{levelLabel(levelKey)} · {schoolYearLabel(schoolYear)}</h2>
              <p>Section + matière permettent au parent de savoir immédiatement à quel cours correspond chaque cahier.</p>
            </div>
            <div className="supplies-heading-actions">
              {!!preset.length && <button type="button" className="button button-secondary" onClick={loadPreset}>
                <WandSparkles size={17}/> Charger la liste fournie
              </button>}
              <button type="button" className="button button-secondary" onClick={() => setItems((current) => [...current, newItem()])}>
                <Plus size={17}/> Ajouter
              </button>
            </div>
          </div>

          <div className="supplies-editor-list">
            {items.map((item, index) => (
              <div className="supplies-editor-row supplies-editor-row-v2" key={item.id}>
                <div className="supplies-row-order">
                  <button type="button" onClick={() => moveItem(item.id, -1)} disabled={index === 0} aria-label="Monter"><ArrowUp size={15}/></button>
                  <button type="button" onClick={() => moveItem(item.id, 1)} disabled={index === items.length - 1} aria-label="Descendre"><ArrowDown size={15}/></button>
                </div>
                <label className="supplies-qty-field"><span>Qté</span><input type="number" min="1" value={item.quantity} onChange={(event) => updateItem(item.id, 'quantity', event.target.value)}/></label>
                <label className="supplies-name-field"><span>Fourniture</span><input dir="auto" value={item.name} onChange={(event) => updateItem(item.id, 'name', event.target.value)} placeholder="Ex. كراس عدد 24"/></label>
                <label><span>Section</span><select value={item.section} onChange={(event) => updateItem(item.id, 'section', event.target.value)}>{SUPPLY_SECTIONS.map((section) => <option key={section}>{section}</option>)}</select></label>
                <label><span>Matière</span><input dir="auto" list="supply-subjects" value={item.subject} onChange={(event) => updateItem(item.id, 'subject', event.target.value)} placeholder="Ex. Français"/></label>
                <label><span>Couverture</span><input dir="auto" value={item.cover} onChange={(event) => updateItem(item.id, 'cover', event.target.value)} placeholder="Ex. غلاف أحمر"/></label>
                <label className="supplies-note-field"><span>Remarque</span><input dir="auto" value={item.note} onChange={(event) => updateItem(item.id, 'note', event.target.value)} placeholder="Optionnel"/></label>
                <button type="button" className="supplies-delete-row" onClick={() => removeItem(item.id)} aria-label="Supprimer"><Trash2 size={17}/></button>
              </div>
            ))}
            <datalist id="supply-subjects">
              {DEFAULT_SUBJECTS.filter(Boolean).map((subject) => <option value={subject} key={subject}/>) }
            </datalist>
            {!items.length && (
              <div className="supplies-empty-actions">
                {!!preset.length && <button type="button" className="supplies-empty-add" onClick={loadPreset}>
                  <WandSparkles size={20}/><strong>Charger la liste de {levelLabel(levelKey)}</strong><span>La liste des photos est déjà retranscrite et organisée.</span>
                </button>}
                <button type="button" className="supplies-empty-add" onClick={() => setItems([newItem()])}>
                  <Plus size={20}/><strong>Créer une liste manuellement</strong><span>Ajoutez les fournitures une par une.</span>
                </button>
              </div>
            )}
          </div>

          <div className="supplies-editor-actions">
            <label className="supplies-publish-toggle">
              <input type="checkbox" checked={published} onChange={(event) => setPublished(event.target.checked)}/>
              <span><strong>Afficher aux parents</strong><small>Décochez pour garder la liste en brouillon.</small></span>
            </label>
            <div>
              {exists && <button type="button" className="button button-secondary supplies-danger-button" onClick={deleteList} disabled={saving}><Trash2 size={16}/> Supprimer</button>}
              <button type="button" className="button button-primary" onClick={saveList} disabled={saving}><Save size={17}/> {saving ? 'Enregistrement…' : 'Enregistrer'}</button>
            </div>
          </div>
        </article>

        <aside className="panel supplies-preview-panel">
          <p className="eyebrow">APERÇU PARENT</p>
          <h3>{levelLabel(levelKey)}</h3>
          <span className="supplies-preview-year">{schoolYearLabel(schoolYear)}</span>
          <div className="supplies-preview-progress"><div style={{ width: '0%' }}/></div>
          <small>0 / {items.filter((item) => item.name.trim()).length} acheté</small>
          <div className="supplies-preview-groups">
            {groupedPreview.map(([section, rows]) => <div key={section} dir="auto">
              <strong>{section}</strong>
              {rows.slice(0, 5).map((item) => <span key={item.id}>
                ☐ {item.quantity > 1 ? `${item.quantity} × ` : ''}{item.name}
                {item.subject ? <em> · {item.subject}</em> : null}
                {item.cover ? <small> — {item.cover}</small> : null}
              </span>)}
              {rows.length > 5 && <small>+ {rows.length - 5} autres</small>}
            </div>)}
            {!groupedPreview.length && <p>Aucune fourniture dans cette liste.</p>}
          </div>
        </aside>
      </section>
    )}
  </>;
}
